import { useEffect, useRef, useState } from "react";
import { HandwritingText } from "../components/ui/handwriting-text";
import { AntiMetalButton } from "../components/ui/anti-metal-button";
import "./GrowthHero.css";

/* ── Animated counter hook ── */
function useCounter(target, decimals = 0) {
  const [val, setVal] = useState(0);
  const triggered = useRef(false);
  const ref = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !triggered.current) {
          triggered.current = true;
          const steps = 70;
          const dur = 1800;
          let i = 0;
          const tick = setInterval(() => {
            i++;
            const p = 1 - Math.pow(1 - i / steps, 3);
            const v = target * p;
            setVal(decimals > 0 ? parseFloat(v.toFixed(decimals)) : Math.floor(v));
            if (i >= steps) { clearInterval(tick); setVal(target); }
          }, dur / steps);
        }
      },
      { threshold: 0.3 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [target, decimals]);

  return [val, ref];
}

/* ── Stat card — matches screenshot layout exactly ── */
function StatCard({ prefix = "", target, suffix = "", decimals = 0, label, desc, bg, textColor, rotate = 0, index = 0 }) {
  const [val, ref] = useCounter(target, decimals);
  const display = decimals > 0 ? val.toFixed(decimals) : val;

  return (
    <div
      ref={ref}
      className="gh-scard"
      style={{
        background: bg,
        color: textColor,
        transform: `rotate(${rotate}deg)`,
        animationDelay: `${0.7 + index * 0.08}s`,
      }}
    >
      {/* Giant number */}
      <div className="gh-scard-num">
        {prefix}{display}{suffix}
      </div>

      {/* Label */}
      <div className="gh-scard-label">{label}</div>

      {/* Rule */}
      <div className="gh-scard-rule" style={{ background: textColor === '#fff' ? 'rgba(255,255,255,0.25)' : 'rgba(0,0,0,0.2)' }} />

      {/* Descriptor */}
      <div className="gh-scard-desc">{desc}</div>
    </div>
  );
}

export default function GrowthHero() {
  return (
    <section className="gh-section">
      {/* Plain black bg + red glow from ::before pseudo */}

      <div className="gh-inner">

        {/* Eyebrow pill */}
        <div className="gh-eyebrow-wrap">
          <span className="gh-eyebrow-dot" />
          <span>Aurelix Growth</span>
        </div>

        {/* Main headline */}
        <h1 className="gh-heading">
          You can buy more traffic. But if the destination doesn't work, you're just paying for{" "}
          <HandwritingText
            words={[
              "broken experiences.",
              "lost conversions.",
              "wasted spend.",
              "missed revenue.",
            ]}
            interval={3200}
            duration={1.5}
            height="1.1em"
            className="gh-hw-word"
          />
        </h1>

        {/* Subtext */}
        <p className="gh-subline">
          Aurelix Growth looks at the full path — from the first interaction
          to the moment someone takes action.
        </p>

        {/* CTA row */}
        <div className="gh-cta-row">
          <AntiMetalButton as="a" href="#growth-services">
            See How It Works
          </AntiMetalButton>
          <a href="#growth-contact" className="gh-link">
            Talk to the team
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
              <path d="M3 8h10M9 4l4 4-4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </a>
        </div>

        {/* Stat cards — fanned layout like screenshot */}
        <div className="gh-scards-wrap">
          <StatCard
            prefix="$" target={32} suffix="M+"
            decimals={0}
            label="Revenue Generated"
            desc="Across 20+ brands in India and the US"
            bg="#ec2b28" textColor="#fff"
            rotate={-4} index={0}
          />
          <StatCard
            prefix="" target={20} suffix="+"
            decimals={0}
            label="Brands Scaled"
            desc="D2C, SaaS, healthcare and more"
            bg="#f5c024" textColor="#0d0d0d"
            rotate={0} index={1}
          />
          <StatCard
            prefix="" target={3.8} suffix="×"
            decimals={1}
            label="Avg. ROAS Uplift"
            desc="Blended return across paid channels"
            bg="#1a1a1a" textColor="#fff"
            rotate={4} index={2}
          />
        </div>

      </div>

      {/* Scroll indicator removed */}
    </section>
  );
}
